import { ClientDevice } from '@ps/platform-message-framework/common/ClientDevice';
import { ClientPlatform } from '@ps/platform-message-framework/common/ClientPlatform';
import { OSId } from '@ps/platform-message-framework/common/OSId';
import { invert } from 'lodash';

import type { Model, SelectorCreator, Slicer } from '../../types';

export interface DeviceState {
  device: ClientDevice;
  os: OSId;
  embedded: boolean;
}

const reducers = {
  update: (state: DeviceState, payload: Partial<DeviceState>) => ({ ...state, ...payload })
};

const selectors = (slice: Slicer<DeviceState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getId() {
    return slice(({ device }) => device);
  },
  getName() {
    return slice(({ device }) => ClientDevice[device]);
  },
  isMobile() {
    return slice(({ device }) => [ClientDevice.PHONE, ClientDevice.TABLET].includes(device));
  },
  isPhone() {
    return slice(({ device }) => device === ClientDevice.PHONE);
  },
  isAndroid() {
    return slice(({ os }) => os === OSId.ANDRO_ID);
  },
  isIos() {
    return slice(({ os }) => os === OSId.I_OS);
  },
  getOS() {
    return slice(({ os }) => os);
  },
  getOSName() {
    return slice((state) => invert(OSId)[state.os]);
  },
  isDesktopEmbedded() {
    return slice(
      ({ embedded, device }) =>
        [ClientDevice.DESKTOP, ClientDevice.OTHER].includes(device) && embedded
    );
  },
  isMobileEmbedded() {
    return slice(
      ({ embedded, device }) =>
        [ClientDevice.PHONE, ClientDevice.TABLET].includes(device) && embedded
    );
  },
  isEmbedded() {
    return slice(({ embedded }) => embedded);
  },
  getPlatform() {
    return slice(({ embedded, os }) => {
      switch (true) {
        case embedded && os === OSId.WINDOWS:
          return ClientPlatform.WIN;
        case embedded && os === OSId.MAC:
          return ClientPlatform.MAC;
        case embedded && os === OSId.ANDRO_ID:
          return ClientPlatform.ANDROID;
        case embedded && os === OSId.I_OS:
          return ClientPlatform.I_OS;
        default:
          return ClientPlatform.WEB;
      }
    });
  }
});

const device: Model<DeviceState, typeof reducers, void, void, typeof selectors> = {
  state: { device: ClientDevice.UNKNOWN, os: OSId.UNKNOWN, embedded: false },
  reducers,
  selectors
};

export default device;
